import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Constants } from '../../admin-core/constants';
import { LogisticTypeService } from '../../admin-core/apiservices';
import { PaginationModel, RequestParamModel } from '../../admin-core/models';
import { HttpErrorResponse } from '@angular/common/http';
import { CarrierService } from '../../admin-core/apiservices/carrier.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {AlertService, DebugLoggerService} from '../../admin-core/services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProductsService } from '@coreapiservices';
import { ScanTrackingNumberComponent } from 'src/app/sample-requests/sample-request-detail/modal/scan-tracking-number/scan-tracking-number.component';
import { AuthService } from '@coreservices';

@Component({
  standalone: false,
  selector: 'app-status-change-confirmation',
  // Eager: this Angular version defaults components to OnPush. The logistic
  // types, carriers, regions and sample boxes are all fetched ASYNC in ngOnInit
  // and set on the component without markForCheck, so an OnPush view never
  // repainted — the logistic-type radios (and other async lists) rendered empty.
  // Same fix as ConfirmModalComponent.
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './status-change-confirmation.component.html',
  styleUrls: ['./status-change-confirmation.component.css', '../../app.component.css']
})
export class StatusChangeConfirmationComponent implements OnInit {
  //#region Variable declaration
  @Input() message!: any;
  @Input() title!: any;
  @Input() isDepositPaid!: boolean;
  @Input() isFinalPaymentPaid!: boolean;
  @Input() isFullPaymentPaid!: boolean;
  @Input() deliveryChargeRequired!: boolean;
  @Input() shipmentReferenceRequired!: boolean;
  @Input() shipmentReference!: any;
  @Input() enableDisableGPSMSyncRequired!: boolean;
  @Input() carrierRequired!: boolean;
  @Input() status: any;
  @Input() order_type: any;
  @Input() type: any;
  @Input() currentStatusId: any;
  @Input() requestType: any;

  public shippingPriceForm!: UntypedFormGroup;
  public carrierForm!: UntypedFormGroup;
  public shippingReferenceForm!: UntypedFormGroup;
  constants = Constants;
  is_send_mail: any;
  submitted: boolean = false;
  payment_mode: any;
  logisticTypes: any;
  requestParamModel!: RequestParamModel;
  isLoading = false;
  is_show_add_carrier = false;
  carriers: any;
  regions: any;
  selectedSampleBox: string
  samplePackingSubmitted: boolean = false
  carrier_submitted: boolean = false;
  shipment_reference_submitted: boolean = false;
  enableDisableGPSMSyncCheckbox: boolean = true;
  trackingNumberMaxChars = 40;
  trackingNumberRole = '';
  shipmentReferenceMaxChars = 15;
  s3BucketImageURL = Constants.S3_PRODUCT_RESOURCE_IMAGE_KIT_URL + '/WEBSITE_ASSET_STAGING';
  hideTrackingNumber: boolean = false;
  adminUserFullName: string;
  isDisplayCameraIcon: boolean = true;
  activeTrackingIndex: number = -1;
  samplePackingBox: any = []
  samplePackingBoxForm!: UntypedFormGroup;
  isCustomOptionSelected = false;
  samplPlaceHolder: any = 'Select Sample Packing Box'
  //#endregion

  //#region constructor
  constructor(
    private fb: UntypedFormBuilder,
    private logisticTypeService: LogisticTypeService,
    public changeDetection: ChangeDetectorRef,
    private carrierService: CarrierService,
    public ngbActiveModal: NgbActiveModal,
    private alertService: AlertService,
    private modalService: NgbModal,
    private productsService: ProductsService,
    private debugLogger: DebugLoggerService,
    private authService: AuthService
  ) {
    let userProfile = JSON.stringify(this.authService.getAdminUser());
    let profile = JSON.parse(userProfile);
    this.adminUserFullName = profile.first_name + ' ' + profile.last_name;
  }

  //#endregion

  //#region functions
  ngOnInit(): void {
    if (this.deliveryChargeRequired) {
        this.loadLogisticTypedata();
    } else if (this.currentStatusId === Constants.SAMPLE_PACKAGES_STATUS_ID) {
        this.loadSampleBoxList();
    } else {
        this.getCarrierList();
        this.getRegionsList();
    }

    this.samplePackingBoxForm = this.fb.group({
      sample_packing_box_id: ['', [Validators.required]],
      custom_packing_box_price: ['', [Validators.required]],
      custom_packing_box_description: ['', [Validators.required]]
    });

    // this.onClose = new Subject();
    this.shippingPriceForm = this.fb.group({
      big_post_delivery_charge: ['', [Validators.required]],
      tracking_numbers: this.fb.array([this.initializeTrackingNumber()]),
      logistic_type_id: ['', [Validators.required]],
      packed_by: ['', [Validators.required]],
    });

    this.carrierForm = this.fb.group({
      carrier_id: ['', [Validators.required]],
      company_name: ['', []],
      contact_name: ['', []],
      contact_mobile_number: ['', []],
      dispatch_email: ['', []],
      applicable_regions: ['', []],
    });

    this.shippingReferenceForm = this.fb.group({
      shipping_reference: ['', []],
      is_domestic_stock_transfer: [''],
    });

    // Subscribe to changes on the sample_packing_box_id form control
   this.samplePackingBoxForm.get('sample_packing_box_id')?.valueChanges.subscribe(selectedId => {
      const selectedBox = this.samplePackingBox.find(box => box.id === selectedId);
      if (selectedBox) {
        // Safely convert price to a number and format
        const price = Number(selectedBox.price);
        const formattedPrice = isNaN(price) ? '0.00' : price.toFixed(2);
        this.samplePackingBoxForm.get('custom_packing_box_price')?.setValue(formattedPrice);
      }
    });

    this.samplePackingBoxForm.get('sample_packing_box_id')?.valueChanges.subscribe(selectedId => {
      const selectedBox = this.samplePackingBox.find(box => box.id === selectedId);
      if (selectedBox) {
        // Safely convert price to a number and format
        const price = Number(selectedBox.price);
        const formattedPrice = isNaN(price) ? '0.00' : price.toFixed(2);
        this.samplePackingBoxForm.get('custom_packing_box_price')?.setValue(formattedPrice);
      }
    });

    this.shippingPriceForm.reset();
    this.carrierForm.reset();
    this.shippingReferenceForm.reset();

    if (this.shipmentReference == Constants.IS_DOMESTIC_STOCK_TRANSFER_NO) {
      this.shippingReferenceForm?.patchValue({
        is_domestic_stock_transfer: '',
      });
    } else {
      this.shippingReferenceForm?.patchValue({
        shipping_reference: this.shipmentReference,
      });
    }
    this.shippingPriceForm.get('packed_by').setValue(this.adminUserFullName);

    this.trackingNumbers.valueChanges.subscribe(() => {
      if (this.isDisplayCameraIcon) {
        this.updateDeliveryCharge();
      }
    });
  }

  /************************************
   * Scan tracking number functions start
   ************************************/
  get trackingNumbers(): UntypedFormArray {
    return this.shippingPriceForm.get('tracking_numbers') as UntypedFormArray;
  }

  initializeTrackingNumber(): UntypedFormGroup {
    return this.fb.group({
      tracking_number: ['', [Validators.required]],
    });
  }

  addTrackingNumber(): void {
    this.trackingNumbers.push(this.initializeTrackingNumber());
  }

  removeTrackingNumber(index: number): void {
    if (this.trackingNumbers.length > 1) {
      this.trackingNumbers.removeAt(index);
      this.updateDeliveryCharge();
    }
  }

  updateDeliveryCharge(): void {
    let totalCharge = null;

    this.trackingNumbers.controls.forEach((control) => {
      const trackingNumber = control.get('tracking_number').value;
      if (trackingNumber) {
        if (
          trackingNumber.startsWith(Constants.METRO_COUPON_START) ||
          trackingNumber.startsWith(Constants.ANOTHER_METRO_COUPON_START)
        ) {
          totalCharge += Constants.METRO_COUPON_VALUE;
        } else if (trackingNumber.startsWith(Constants.LINK_COUPON_START)) {
          totalCharge += Constants.LINK_COUPON_VALUE;
        }
      }
    });

    const allTrackingNumbersFilled = this.trackingNumbers.controls.every(
      (control) => control.get('tracking_number').value?.trim() !== ''
    );

    if (!allTrackingNumbersFilled) {
      totalCharge = null;
    } else {
      totalCharge = Number(totalCharge).toFixed(2);
    }

    this.shippingPriceForm
      .get('big_post_delivery_charge')
      .setValue(totalCharge, { emitEvent: false });
  }

  get hasInvalidTrackingNumber(): boolean {
    return this.trackingNumbers.controls.some(
      (control) =>
        control.get('tracking_number').invalid &&
        (control.get('tracking_number').dirty ||
          control.get('tracking_number').touched)
    );
  }

  isInvalidTrackingNumber(): boolean {
    let invalidTrackingNumber = this.trackingNumbers.controls.some(
      (control) =>
        control.get('tracking_number').invalid &&
        (control.get('tracking_number').dirty ||
          control.get('tracking_number').touched ||
          this.submitted)
    );
    return invalidTrackingNumber;
  }

  hasTrackingNumberError(): boolean {
    return this.trackingNumbers.controls.some(
      (control) => control.get('tracking_number').errors?.required
    );
  }

  checkForCamera(index: number): void {
    this.activeTrackingIndex = index;
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      const videoInputDevices = devices.filter(
        (device) => device.kind === 'videoinput'
      );
      let hasCamera = videoInputDevices.length > 0;

      if (!hasCamera) {
        this.alertService.error('No camera found.');
        return;
      } else {
        this.requestCameraAccess();
      }
    });
  }

  requestCameraAccess() {
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        stream.getTracks().forEach((track) => track.stop());
        this.displayBarcodeScanner();
      })
      .catch((err) => {
        if (
          err.name === Constants.NOT_ALLOWED_ERROR ||
          err.name === Constants.PERMISSION_DENIED_ERROR
        ) {
          this.alertService.error(Constants.CAMERA_ACCESS_DENIED_ERROR_MESSAGE);
        }
      });
  }

  displayBarcodeScanner(): void {
    const instance = this.modalService.open(ScanTrackingNumberComponent, {
      centered: false,
      windowClass: 'update-contact-modal upload-product-image-modal',
    });
    instance.closed.subscribe((result) => {
      if (result) {
        this.trackingNumbers
          .at(this.activeTrackingIndex)
          .get('tracking_number')
          .setValue(result);
        this.updateDeliveryCharge();
      }
    });
  }

  /************************************
   * Scan tracking number functions end
   ************************************/

  loadLogisticTypedata() {
    this.isLoading = true;
    let requestParamModel = new RequestParamModel();
    requestParamModel.pageSize = 10;
    requestParamModel.fields = ['id', 'name'];
    this.logisticTypeService.getLogisticTypes(requestParamModel).subscribe(
      (dataList: PaginationModel<any>) => {
        this.logisticTypes = dataList.data;

        const goLogisticsIndex = this.logisticTypes.findIndex(
          (type) => type.id === Constants.LOGISTIC_TYPE_IDS.GO_LOGISTIC
        );

        if (goLogisticsIndex > -1) {
          const goLogistics = this.logisticTypes.splice(goLogisticsIndex, 1)[0];

          this.logisticTypes.splice(0, 0, goLogistics);
        }

        if (this.logisticTypes.length > 0) {
          this.shippingPriceForm.patchValue({
            logistic_type_id: this.logisticTypes[0].id.toString(),
          });
        }

        this.isLoading = false;
      },
      (error: HttpErrorResponse) => {
        this.isLoading = false;
      }
    );
  }

  setLogisticType(event: any) {
    if (event == Constants.LOGISTIC_TYPE_IDS.BIGPOST || event == Constants.LOGISTIC_TYPE_IDS.AUS_POST || event == Constants.LOGISTIC_TYPE_IDS.OTHER) {
      this.isDisplayCameraIcon = false;

      const numberOfFieldsToRemove = this.trackingNumbers.length - 1;
      if (numberOfFieldsToRemove > 0) {
        for (let i = 0; i < numberOfFieldsToRemove; i++) {
          this.trackingNumbers.removeAt(1);
        }
      }
      this.trackingNumbers.reset();
      this.shippingPriceForm.get('big_post_delivery_charge').setValue('');
    } else {
      this.isDisplayCameraIcon = true;
      this.trackingNumbers.reset();
      this.shippingPriceForm.get('big_post_delivery_charge').setValue('');
    }
    if (event == Constants.LOGISTIC_TYPE_IDS.OTHER) {
      this.hideTrackingNumber = true;
    } else {
      this.hideTrackingNumber = false;
    }
    this.shippingPriceForm.patchValue({
      logistic_type_id: event.toString(),
    });
  }

  onTrackingNumberKeyup(event: KeyboardEvent, index: number): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;

    if (value && this.isDisplayCameraIcon) {
      if (
        !(
          value.startsWith(Constants.METRO_COUPON_START) ||
          value.startsWith(Constants.ANOTHER_METRO_COUPON_START) ||
          value.startsWith(Constants.LINK_COUPON_START)
        )
      ) {
        this.alertService.error(Constants.TRACKING_NUMBER_STARTS_WITH);
      }
    }
  }

  get sbf() {
    return this.samplePackingBoxForm.controls;
  }

  get sf() {
    return this.shippingPriceForm.controls;
  }

  get cf() {
    return this.carrierForm.controls;
  }

  get shp() {
    return this.shippingReferenceForm.controls;
  }

  confirm(): void {
    let model: any = {};
    model.is_send_mail = this.is_send_mail;
    model.is_save = true;
    model.payment_mode = this.payment_mode;
    model.requestType = this.requestType;
    model.is_converted = true;
    this.ngbActiveModal.close(model);
  }

  decline(): void {
    const status = this.requestType == this.constants.SAMPLE_REQUEST_CLOSE ? {is_converted: false,requestType: this.requestType} : false;
    this.ngbActiveModal.close(status);
    return;
  }

  checkSendMail($event: any) {
    if ($event.target.checked) {
      this.is_send_mail = true;
    } else {
      this.is_send_mail = false;
    }
  }

  //#endregion

  depositPaymentOptionDisable() {
    return (
      this.status === 'Final Payment Paid' ||
      this.status === 'Goods Despatch Out To Tender' ||
      this.status === 'Goods Despatched' ||
      this.status === 'Goods Despatch Despatched' ||
      this.status === 'Complete' ||
      this.status === 'Follow Up'
    );
  }

  onSubmit() {
    this.submitted = true;
    if (this.isDisplayCameraIcon) {
      this.isInvalidTrackingNumber();
      let isTrackingNumberNotValid = this.trackingNumbers.controls.some(
        (control) => {
          return (
            !(
              control
                .get('tracking_number')
                ?.value?.startsWith(Constants.METRO_COUPON_START) ||
              control
                .get('tracking_number')
                ?.value?.startsWith(Constants.ANOTHER_METRO_COUPON_START) ||
              control
                .get('tracking_number')
                ?.value?.startsWith(Constants.LINK_COUPON_START)
            ) && control.get('tracking_number')?.valid
          );
        }
      );

      if (isTrackingNumberNotValid) {
        this.alertService.error(Constants.TRACKING_NUMBER_STARTS_WITH);
        return;
      }
    }
    if (Number(this.shippingPriceForm.get('logistic_type_id')?.value) !== Constants.LOGISTIC_TYPE_IDS.OTHER) {
      this.trackingNumbers.controls.forEach((control) => {
        control.get('tracking_number')?.setValidators([Validators.required]);
        control.get('tracking_number')?.updateValueAndValidity();
      });
    } else {
      this.trackingNumbers.controls.forEach((control) => {
        control.get('tracking_number')?.clearValidators();
        control.get('tracking_number')?.updateValueAndValidity();
      });
      this.trackingNumbers.controls.forEach((control) => {
        control.get('tracking_number')?.setValue(null);
      });
    }

    // Check if the form is invalid
    if (this.shippingPriceForm.invalid) {
      return;
    }


    let model: any = {};
    model.big_post_delivery_charge = this.sf.big_post_delivery_charge.value;

    if (model.big_post_delivery_charge < 10000) {
      // Proceed with the form submission logic
    } else {
      this.alertService.error(Constants.DELIVERY_CHARGE);
      return false;
    }

    model.logistic_type_id = this.sf.logistic_type_id.value;

    /* ################### Consider First Tracking Number For This Sample Start ################### */
    if (this.trackingNumbers && this.trackingNumbers.length > 0) {
      var trackingNumbers = this.trackingNumbers.value;
      if (trackingNumbers && trackingNumbers.length > 0) {
        model.tracking_numbers = trackingNumbers;
        if (trackingNumbers[0]) {
          var primaryTrackingNumber = trackingNumbers[0];
          model.tracking_number = primaryTrackingNumber.tracking_number;
        }
      }
    }
    /* ################### Consider First Tracking Number For This Sample End ################### */

    model.packed_by = this.sf.packed_by.value;
    model.is_save = true;

    this.ngbActiveModal.close(model);
  }

  onSubmitSampleBox() {
    if (this.samplePackingBoxForm.invalid) {
      this.samplePackingSubmitted = true;
      return;
    }
    const sampleModel = this.samplePackingBoxForm.value;
    this.ngbActiveModal.close(sampleModel);
  }

  getCarrierList() {
    this.changeDetection.markForCheck();
    this.carriers = [];
    this.isLoading = true;
    let requestParamModel = new RequestParamModel();
    requestParamModel.pageSize = 'all';
    this.carrierService.getCarriers(requestParamModel).subscribe(
      (dataList: any) => {
        this.isLoading = false;
        this.carriers = dataList.data;
        this.selectAllForDropdownItems(this.carriers);
      },
      (error: HttpErrorResponse) => {
        this.isLoading = false;
      }
    );
  }

  getRegionsList() {
    this.changeDetection.markForCheck();
    this.regions = [];
    this.isLoading = true;
    const requestParamModel = new RequestParamModel();
    requestParamModel.pageSize = Constants.PAGE_SIZE_ALL;
    this.carrierService.getRegions(requestParamModel).subscribe(
      (dataList: any) => {
        this.isLoading = false;
        this.regions = dataList;
      },
      (error: HttpErrorResponse) => {
        this.isLoading = false;
      }
    );
  }

  hideShowAddCarrierData() {
    this.is_show_add_carrier = !this.is_show_add_carrier;

    if (this.is_show_add_carrier) {
      this.carrierForm.controls['carrier_id'].disable();

      this.carrierForm
        .get('company_name')
        ?.setValidators([Validators.required]);
      this.carrierForm
        .get('contact_name')
        ?.setValidators([Validators.required]);
      this.carrierForm
        .get('contact_mobile_number')
        ?.setValidators([
          Validators.maxLength(10),
          Validators.minLength(10),
          this.mobileValidator.bind(this)
        ]);
      this.carrierForm
        .get('dispatch_email')
        ?.setValidators([
          Validators.required,
          Validators.email,
          Validators.pattern(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/),
        ]);
      this.carrierForm
        .get('applicable_regions')
        ?.setValidators([Validators.required]);
      this.carrierForm?.get('carrier_id')!.clearValidators();

      this.changeDetection.markForCheck();
    } else {
      this.carrierForm.controls['carrier_id'].enable();

      this.carrierForm.get('company_name')!.clearValidators();
      this.carrierForm.get('contact_name')!.clearValidators();
      this.carrierForm.get('contact_mobile_number')!.clearValidators();
      this.carrierForm.get('dispatch_email')!.clearValidators();
      this.carrierForm.get('applicable_regions')!.clearValidators();
      this.carrierForm.get('carrier_id')?.setValidators([Validators.required]);

      this.changeDetection.markForCheck();
    }

    this.carrierForm.get('company_name')?.updateValueAndValidity();
    this.carrierForm.get('contact_name')?.updateValueAndValidity();
    this.carrierForm.get('contact_mobile_number')?.updateValueAndValidity();
    this.carrierForm.get('dispatch_email')?.updateValueAndValidity();
    this.carrierForm.get('applicable_regions')?.updateValueAndValidity();
    this.carrierForm.get('carrier_id')?.updateValueAndValidity();
  }

  private mobileValidator(control: UntypedFormControl): { [key: string]: boolean } | null {
    const value = control.value?.trim(); // Trim whitespace for safety

    if (!value) {
      return null; // Allow null or empty values
    }

    const isMobile = /^04\d{8}$/.test(value); // Australian mobile: Starts with '04' + 8 digits
    const isLandline = /^(0[2|3|7|8])\d{8}$/.test(value); // Australian landline: Starts with 02, 03, 07, or 08 + 8 digits

    return isMobile || isLandline ? null : { mobileNotValid: true }; // Return error only if invalid
  }

  selectAllForDropdownItems(items: any[]) {
    let allSelect = (items: any) => {
      items.forEach((element: any) => {
        element['selectedAllGroup'] = 'selectedAllGroup';
      });
    };

    allSelect(items);
  }

  onSubmitCarrier() {
    this.carrier_submitted = true;
    if (this.carrierForm.invalid) {
      return;
    }
    let carrier_model: any = {};
    carrier_model.is_show_add_carrier = this.is_show_add_carrier;
    carrier_model.carrier_id = this.cf.carrier_id.value;
    carrier_model.company_name = this.cf.company_name.value;
    carrier_model.contact_name = this.cf.contact_name.value;
    carrier_model.contact_mobile_number = this.cf.contact_mobile_number.value;
    carrier_model.dispatch_email = this.cf.dispatch_email.value;
    carrier_model.applicable_regions = this.cf.applicable_regions.value;
    carrier_model.is_carrier_save = true;

    carrier_model.is_send_mail = this.is_send_mail;
    carrier_model.payment_mode = this.payment_mode;
    carrier_model.type = this.type;

    this.ngbActiveModal.close(carrier_model);
  }

  checkDecimalDot(e: any) {
    var charCode = e.which ? e.which : e.keyCode;
    if (charCode != 46 && charCode > 31 && (charCode < 48 || charCode > 57))
      return false;
  }

  onSubmitShipmentReference() {
    this.shipment_reference_submitted = true;
    let shipmentModel: any = {};
    shipmentModel.is_save = true;
    if (this.shp.is_domestic_stock_transfer.value) {
      shipmentModel.is_shipment_reference_save = false;
      shipmentModel.is_domestic_stock_transfer =
        Constants.IS_DOMESTIC_STOCK_TRANSFER_YES;
    } else {
      shipmentModel.is_shipment_reference_save = true;
      shipmentModel.shipping_reference = this.shp.shipping_reference.value;
      shipmentModel.is_domestic_stock_transfer =
        Constants.IS_DOMESTIC_STOCK_TRANSFER_NO;
    }
    this.ngbActiveModal.close(shipmentModel);
  }

  onSubmitEnableDisableGPSMSync() {
    let GPSMSyncModel: any = {};
    GPSMSyncModel.is_save = true;
    if (this.enableDisableGPSMSyncCheckbox) {
      GPSMSyncModel.is_disable_gpsm_sync = this.enableDisableGPSMSyncCheckbox;
    }
    this.ngbActiveModal.close(GPSMSyncModel);
  }

  changeValue(event: any) {
    this.enableDisableGPSMSyncCheckbox = event?.target?.checked;
  }

  keyPressAlphaNumeric(event: any) {
    var inp = String.fromCharCode(event.keyCode);

    if (/[a-zA-Z0-9]/.test(inp)) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  enableDisableShipmentReference($event: any) {
    if ($event.target.checked) {
      this.shippingReferenceForm.get('shipping_reference').setValue('');
      this.shippingReferenceForm.get('shipping_reference').disable();
    } else {
      this.shippingReferenceForm.get('shipping_reference').enable();
    }
  }

  // Function Start for sample Packged
  loadSampleBoxList() {
    this.samplePackingBox = [];
    this.isLoading = true;
    const requestParams = new RequestParamModel();
    requestParams.pageSize = 'all';
    this.productsService.getSamplePackingBox(requestParams).subscribe(
      (dataList: PaginationModel<any>) => {
        this.isLoading = false;
        this.samplePackingBox = [...dataList.data, {
          id: Constants.CUSTOM_SAMPLE_PACKING_BOX_ID,
          ul_code: "CUSTOM",
          name: "Custom",
          price: null,
          displayLabel: "CUSTOM - Custom"
        }];
      },
      (error: HttpErrorResponse) => {
        this.isLoading = false;
      }
    );
  }

  onSampleBoxChange(event: any): void {
    // this.debugLogger.log('Constants.CUSTOM_SAMPLE_PACKING_BOX_ID', Constants.CUSTOM_SAMPLE_PACKING_BOX_ID);
    if (event.id === Constants.CUSTOM_SAMPLE_PACKING_BOX_ID) {
      this.isCustomOptionSelected = true;
      this.samplePackingBoxForm.get('custom_packing_box_price').enable();
      this.samplePackingBoxForm.get('custom_packing_box_price').reset();
      this.samplePackingBoxForm.get('custom_packing_box_description').setValidators(Validators.required);
    } else {
      this.isCustomOptionSelected = false;
      this.samplePackingBoxForm.get('custom_packing_box_price').disable();  // Disable the input field
      this.samplePackingBoxForm.get('custom_packing_box_description').clearValidators();
    }
    this.samplePackingBoxForm.get('custom_packing_box_description').updateValueAndValidity();
  }

  onSampleBoxClear() {
    this.samplePackingBoxForm?.get('custom_packing_box_price').reset();
  }

  closeModal() {
    this.ngbActiveModal.close(false);
    return
  }

  // Function End for sample Packged

}
